import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminSidebar } from '@/config/sidebarConfig';
import { getUsersApi } from '@/lib/api/usersApi';
import { impersonateApi } from '@/lib/auth/authApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { updateSearchParams } from '@/lib/url-params';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, formatDateTimeRelative, getInitials } from '@shared';
import { Role } from '@shared/enums/role.enum';
import type { AuthUser } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Briefcase,
  Building,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  LogIn,
  PauseCircle,
  Search,
  ShieldAlert,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';

import UserDetailDrawer from '../../components/admin/UserDetailDrawer';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

const FRONT_URL = (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5232';

// ─── Role display config ────────────────────────────────
const ROLE_CONFIG: Record<Role, { label: string; bg: string; text: string; icon: LucideIcon }> = {
  [Role.SUPER_ADMIN]: { label: 'Super Admin', bg: 'bg-red-100', text: 'text-red-700', icon: ShieldAlert },
  [Role.ORGANIZATION_ADMIN]: { label: 'Organisateur', bg: 'bg-amber-100', text: 'text-amber-700', icon: Briefcase },
  [Role.CLUB_ADMIN]: { label: 'Club', bg: 'bg-purple-100', text: 'text-purple-700', icon: Building },
  [Role.MEMBER]: { label: 'Joueur', bg: 'bg-blue-100', text: 'text-blue-700', icon: User },
};

const VALID_ROLES = new Set<string>(Object.values(Role));
const VALID_DISABLED = new Set(['true', 'false']);
const VALID_DELETED = new Set(['all', 'only']);

// ─── Page component ──────────────────────────────────────
export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  // ─── Read filters from URL ───
  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));
  const urlRole = searchParams.get('role') ?? '';
  const urlDisabled = searchParams.get('disabled') ?? '';
  const urlDeleted = searchParams.get('deleted') ?? '';

  const roleFilter = (VALID_ROLES.has(urlRole) ? urlRole : '') as Role | '';
  const disabledFilter = (VALID_DISABLED.has(urlDisabled) ? urlDisabled : '') as '' | 'true' | 'false';
  const deletedFilter = (VALID_DELETED.has(urlDeleted) ? urlDeleted : '') as '' | 'all' | 'only';
  // pageIndex is 0-based (TanStack Table), page in URL is 1-based
  const pageIndex = urlPage - 1;

  // ─── searchInput is local (debounce buffer); search is URL-persisted ───
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = searchParams.get('search') ?? '';

  const [impersonating, setImpersonating] = useState<number | null>(null);
  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const currentUser = useAuthStore(s => s.user);

  const pagination: PaginationState = { pageIndex, pageSize: 20 };

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => updateSearchParams(prev, updates), { replace: true });
  }

  const queryParams = {
    page: urlPage,
    limit: pagination.pageSize,
    ...(search && { search }),
    ...(roleFilter && { role: roleFilter }),
    ...(disabledFilter !== '' && { disabled: disabledFilter === 'true' }),
    ...(deletedFilter !== '' && { includeDeleted: true }),
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.users.list(queryParams),
    queryFn: () => getUsersApi(queryParams),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 350);
  }

  function handleRoleChange(value: Role | '') {
    updateParams({ role: value || undefined, page: undefined });
  }

  function handleDisabledChange(value: '' | 'true' | 'false') {
    updateParams({ disabled: value || undefined, page: undefined });
  }

  function handleDeletedChange(value: '' | 'all' | 'only') {
    updateParams({ deleted: value || undefined, page: undefined });
  }

  async function handleImpersonate(user: AuthUser) {
    if (impersonating) return;
    setImpersonating(user.id);
    try {
      const res = await impersonateApi(user.id);
      const hash = new URLSearchParams({ token: res.accessToken }).toString();
      window.open(`${FRONT_URL}/auth/impersonate#${hash}`, '_blank', 'noopener');
    } catch {
      toast.error("Impossible d'impersonner cet utilisateur");
    } finally {
      setImpersonating(null);
    }
  }

  const columns = useMemo<ColumnDef<AuthUser>[]>(
    () => [
      {
        id: 'user',
        header: 'Utilisateur',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-sm font-bold text-white'>
                {getInitials(u.firstName, u.lastName)}
              </div>
              <div>
                <p className='font-semibold text-slate-900'>
                  {u.firstName} {u.lastName}
                </p>
                <p className='text-xs text-slate-500'>@{u.username ?? u.email}</p>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'role',
        header: 'Rôle',
        cell: ({ row }) => {
          const cfg = ROLE_CONFIG[row.original.role];
          const Icon = cfg.icon;
          return (
            <span
              className={`flex w-fit items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
            >
              <Icon className='h-3 w-3' />
              {cfg.label}
            </span>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email / Tél',
        cell: ({ row }) => (
          <div>
            <p className='text-sm text-slate-900'>{row.original.email}</p>
            <p className='text-xs text-slate-500'>{row.original.mobile ?? '—'}</p>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Statut',
        cell: ({ row }) => {
          const u = row.original;
          if (u.deletedAt) {
            return (
              <span className='flex w-fit items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700'>
                <Trash2 className='h-3 w-3' />
                Supprimé
              </span>
            );
          }
          if (u.disabled) {
            return (
              <span className='flex w-fit items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700'>
                <PauseCircle className='h-3 w-3' />
                Désactivé
              </span>
            );
          }
          return (
            <span className='rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700'>Actif</span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Inscription',
        cell: ({ row }) => <p className='text-sm text-slate-900'>{formatDateAbbreviatedFr(row.original.createdAt)}</p>,
      },
      {
        accessorKey: 'lastLogin',
        header: 'Dernière connexion',
        cell: ({ row }) => (
          <p
            className={`text-sm ${row.original.disabled || row.original.deletedAt ? 'text-slate-400' : 'text-slate-900'}`}
          >
            {formatDateTimeRelative(row.original.lastLogin)}
          </p>
        ),
      },
      {
        id: 'actions',
        header: () => <span className='sr-only'>Actions</span>,
        cell: ({ row }) => {
          const u = row.original;
          const canImpersonate = u.role !== Role.SUPER_ADMIN && u.id !== currentUser?.id;
          return (
            <div className='flex items-center justify-end gap-2'>
              <button
                onClick={() => setDetailUserId(u.id)}
                className='rounded-lg p-2 transition hover:bg-slate-100'
                title={`Voir ${u.firstName} ${u.lastName}`}
                aria-label={`Voir ${u.firstName} ${u.lastName}`}
              >
                <Eye className='h-4 w-4 text-slate-600' aria-hidden='true' />
              </button>
              {canImpersonate && (
                <button
                  onClick={() => handleImpersonate(u)}
                  disabled={impersonating === u.id}
                  className='rounded-lg p-2 transition hover:bg-amber-50 disabled:opacity-40'
                  title={`Impersonner ${u.firstName} ${u.lastName}`}
                  aria-label={`Impersonner ${u.firstName} ${u.lastName}`}
                >
                  <LogIn className='h-4 w-4 text-amber-600' aria-hidden='true' />
                </button>
              )}
              <Link
                to={`/admin/users/${u.id}/edit`}
                className='rounded-lg p-2 transition hover:bg-slate-100'
                title={`Modifier ${u.firstName} ${u.lastName}`}
                aria-label={`Modifier ${u.firstName} ${u.lastName}`}
              >
                <Edit className='h-4 w-4 text-slate-600' aria-hidden='true' />
              </Link>
            </div>
          );
        },
      },
    ],
    [currentUser?.id, impersonating], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    pageCount: data?.meta?.totalPages ?? -1,
    state: { pagination },
    onPaginationChange: updater => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      updateParams({ page: String(next.pageIndex + 1) });
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const total = data?.meta?.total ?? 0;
  const from = total === 0 ? 0 : pageIndex * pagination.pageSize + 1;
  const to = Math.min((pageIndex + 1) * pagination.pageSize, total);
  const pageCount = table.getPageCount();
  const currentPage = pageIndex;

  // Pages to display around current page
  function getPageNumbers(): (number | '...')[] {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
    const pages: (number | '...')[] = [0];
    if (currentPage > 2) pages.push('...');
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(pageCount - 2, currentPage + 1); i++) pages.push(i);
    if (currentPage < pageCount - 3) pages.push('...');
    pages.push(pageCount - 1);
    return pages;
  }

  const isPageLoading = isLoading || !isAuthenticated;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8'>
        <PageHeader title='Gestion des Utilisateurs' subtitle='Administration complète des comptes utilisateurs' />

        {/* Filters */}
        <div className='mb-6 rounded-xl border border-slate-200 bg-white p-6'>
          <div className='flex items-center gap-4'>
            <div className='relative max-w-md flex-1'>
              <Search className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400' />
              <input
                type='text'
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder='Rechercher par nom, email...'
                className='w-full rounded-lg border border-slate-200 py-2 pl-10 pr-4 focus:border-red-500 focus:outline-none'
              />
            </div>

            <select
              value={roleFilter}
              onChange={e => handleRoleChange(e.target.value as Role | '')}
              className='rounded-lg border border-slate-200 px-4 py-2 focus:border-red-500 focus:outline-none'
            >
              <option value=''>Tous les rôles</option>
              <option value={Role.SUPER_ADMIN}>Super Admin</option>
              <option value={Role.MEMBER}>Joueur</option>
              <option value={Role.CLUB_ADMIN}>Club</option>
              <option value={Role.ORGANIZATION_ADMIN}>Organisateur</option>
            </select>

            <select
              value={disabledFilter}
              onChange={e => handleDisabledChange(e.target.value as '' | 'true' | 'false')}
              className='rounded-lg border border-slate-200 px-4 py-2 focus:border-red-500 focus:outline-none'
            >
              <option value=''>Tous les statuts</option>
              <option value='false'>Actif</option>
              <option value='true'>Désactivé</option>
            </select>

            <select
              value={deletedFilter}
              onChange={e => handleDeletedChange(e.target.value as '' | 'all' | 'only')}
              className='rounded-lg border border-slate-200 px-4 py-2 focus:border-red-500 focus:outline-none'
            >
              <option value=''>Masquer supprimés</option>
              <option value='all'>Inclure supprimés</option>
              <option value='only'>Uniquement supprimés</option>
            </select>
          </div>
        </div>

        {/* Stat : total */}
        <div className='mb-6 rounded-xl border border-slate-200 bg-white p-6'>
          <div className='flex items-center gap-4'>
            <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100'>
              <Users className='h-6 w-6 text-blue-600' />
            </div>
            <div>
              <p className='text-3xl font-bold text-slate-900'>{isPageLoading ? '…' : total.toLocaleString('fr-FR')}</p>
              <p className='text-sm text-slate-600'>Utilisateurs au total</p>
            </div>
            {isFetching && !isLoading && <span className='ml-auto text-xs text-slate-400'>Actualisation…</span>}
          </div>
        </div>

        {isError && <QueryErrorState onRetry={() => void refetch()} />}

        {/* Table */}
        <div className='rounded-xl border border-slate-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='border-b border-slate-200 bg-slate-50'>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id}>
                    {hg.headers.map(header => (
                      <th
                        key={header.id}
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase text-slate-600'
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className='divide-y divide-slate-200'>
                {isPageLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {columns.map((_, j) => (
                        <td key={j} className='px-6 py-4'>
                          <div className='h-4 animate-pulse rounded bg-slate-200' />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className='px-6 py-12 text-center text-sm text-slate-500'>
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className={`hover:bg-slate-50 ${row.original.disabled || row.original.deletedAt ? 'opacity-60' : ''}`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className='px-6 py-4'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
            <p className='text-sm text-slate-600'>
              {total === 0
                ? 'Aucun résultat'
                : `Affichage de ${from} à ${to} sur ${total.toLocaleString('fr-FR')} utilisateurs`}
            </p>
            <div className='flex items-center gap-2'>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className='flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 transition hover:bg-slate-50 disabled:opacity-40'
              >
                <ChevronLeft className='h-4 w-4' />
                Précédent
              </button>

              {getPageNumbers().map((page, i) =>
                page === '...' ? (
                  <span key={`dots-${i}`} className='px-2 text-slate-400'>
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => table.setPageIndex(page as number)}
                    aria-current={page === currentPage ? 'page' : undefined}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      page === currentPage ? 'bg-red-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {(page as number) + 1}
                  </button>
                ),
              )}

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className='flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 transition hover:bg-slate-50 disabled:opacity-40'
              >
                Suivant
                <ChevronRight className='h-4 w-4' />
              </button>
            </div>
          </div>
        </div>
      </div>

      <UserDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} />
    </DashboardLayout>
  );
}
